/**
 * LeaderReviewLive.tsx
 * Admin-only live session dashboard.
 * Select a leader, see their self-score vs peer-avg per muscle side-by-side.
 * Add live notes + optional live score override. Advance to consolidation.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchSession, fetchEnrollments, fetchLiveNotes, addLiveNote,
  advanceSessionStatus, aggregatePeerScores,
  fetchSelfAssessment as fetchSelf,
  fetchRatingsForLeader, resolveSessionMuscles,
} from '../services/leaderReviewService';
import { LEADER_MUSCLES } from '../types';
import type {
  LeaderReviewSession, LeaderReviewEnrollment, LeaderReviewLiveNote,
  LeaderMuscleScore, LeaderMuscle,
} from '../types';

// ─── Muscle bar row ───────────────────────────────────────────────────────────

interface MuscleBarProps {
  muscle: { name: string; shortDesc: string };
  selfScore: number;
  peerScore: number;
}

function MuscleBar({ muscle, selfScore, peerScore }: MuscleBarProps) {
  return (
    <div className="py-3 border-b border-gray-50 last:border-0">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <span className="text-sm font-semibold text-gray-800">{muscle.name}</span>
          <span className="text-xs text-gray-400 ml-2">{muscle.shortDesc}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#011132] font-semibold">Self: {selfScore > 0 ? selfScore.toFixed(1) : '—'}</span>
          <span className="text-blue-600 font-semibold">Peers: {peerScore > 0 ? peerScore.toFixed(1) : '—'}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden relative">
          {/* Peer bar (background) */}
          <div
            className="absolute inset-y-0 left-0 bg-blue-200 rounded-full transition-all"
            style={{ width: `${(peerScore / 5) * 100}%` }}
          />
          {/* Self bar (overlay) */}
          <div
            className="absolute inset-y-0 left-0 bg-[#011132] rounded-full transition-all opacity-80"
            style={{ width: `${(selfScore / 5) * 100}%` }}
          />
        </div>
        <div className="flex gap-0.5 flex-shrink-0">
          {[1,2,3,4,5].map(n => (
            <div
              key={n}
              className={`w-1 h-4 rounded-sm ${n <= Math.round((selfScore + peerScore) / 2) ? 'bg-[#011132]' : 'bg-gray-100'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const LeaderReviewLive: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user }      = useAuth();
  const navigate      = useNavigate();

  const isHR = ['superadmin', 'admin', 'hr_admin'].includes(user?.role ?? '');
  // isFacilitator is computed after session loads — see below

  const [session, setSession]           = useState<LeaderReviewSession | null>(null);
  const [muscles, setMuscles]           = useState<LeaderMuscle[]>(LEADER_MUSCLES);
  const [enrollments, setEnrollments]   = useState<LeaderReviewEnrollment[]>([]);
  const [selectedLeader, setSelectedLeader] = useState<LeaderReviewEnrollment | null>(null);
  const [selfScores, setSelfScores]     = useState<LeaderMuscleScore[]>([]);
  const [peerAvgScores, setPeerAvgScores] = useState<LeaderMuscleScore[]>([]);
  const [liveNotes, setLiveNotes]       = useState<LeaderReviewLiveNote[]>([]);
  const [noteText, setNoteText]         = useState('');
  const [liveScore, setLiveScore]       = useState<number | ''>('');
  const [loading, setLoading]           = useState(true);
  const [advancing, setAdvancing]       = useState(false);
  const [addingNote, setAddingNote]     = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const [sess, enrolls] = await Promise.all([fetchSession(sessionId), fetchEnrollments(sessionId)]);
      setSession(sess);
      setEnrollments(enrolls);
      if (enrolls.length > 0 && !selectedLeader) setSelectedLeader(enrolls[0]);
      if (sess) {
        const resolvedMuscles = await resolveSessionMuscles(sess);
        setMuscles(resolvedMuscles);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedLeader]);

  useEffect(() => { load(); }, [load]);

  const loadLeaderData = useCallback(async () => {
    if (!sessionId || !selectedLeader) return;
    const [self, ratings, notes] = await Promise.all([
      fetchSelf(sessionId, selectedLeader.leaderId),
      fetchRatingsForLeader(sessionId, selectedLeader.leaderId),
      fetchLiveNotes(sessionId, selectedLeader.leaderId),
    ]);
    setSelfScores(self?.scores ?? []);
    setPeerAvgScores(aggregatePeerScores(ratings));
    setLiveNotes(notes);
  }, [sessionId, selectedLeader]);

  useEffect(() => { loadLeaderData(); }, [loadLeaderData]);

  const handleSelectLeader = (e: LeaderReviewEnrollment) => {
    setSelectedLeader(e);
    setSelfScores([]);
    setPeerAvgScores([]);
    setLiveNotes([]);
    setNoteText('');
    setLiveScore('');
  };

  const handleAddNote = async () => {
    if (!sessionId || !user?.uid || !selectedLeader || !noteText.trim()) return;
    setAddingNote(true);
    try {
      await addLiveNote({
        sessionId,
        leaderId: selectedLeader.leaderId,
        authorId: user.uid,
        content: noteText,
        liveScore: liveScore !== '' ? Number(liveScore) : undefined,
      });
      setNoteText('');
      setLiveScore('');
      const notes = await fetchLiveNotes(sessionId, selectedLeader.leaderId);
      setLiveNotes(notes);
    } finally {
      setAddingNote(false);
    }
  };

  const handleAdvance = async () => {
    if (!session) return;
    setAdvancing(true);
    try {
      await advanceSessionStatus(session.id, 'consolidation');
      navigate(`/leader-review/results/${session.id}`);
    } finally {
      setAdvancing(false);
    }
  };

  const getSelfScore  = (key: string) => selfScores.find(s => s.muscleKey === key)?.score ?? 0;
  const getPeerScore  = (key: string) => peerAvgScores.find(s => s.muscleKey === key)?.score ?? 0;
  const avgSelf       = selfScores.length ? selfScores.reduce((s, m) => s + m.score, 0) / selfScores.length : 0;
  const avgPeer       = peerAvgScores.length ? peerAvgScores.reduce((s, m) => s + m.score, 0) / peerAvgScores.length : 0;

  // Facilitator = HR OR the leader designated as session facilitator OR the session creator
  const isFacilitator = isHR ||
    (!!user?.uid && (
      session?.facilitatorId === user.uid ||
      session?.createdBy === user.uid
    ));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-[#011132] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="h-full flex flex-col">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/leader-review')} className="text-gray-400 hover:text-gray-600">
            <span className="material-symbols-rounded" style={{ fontSize: 20 }}>arrow_back</span>
          </button>
          <div>
            <h1 className="text-sm font-bold text-gray-900">Live Session — {session?.name}</h1>
            <p className="text-xs text-gray-400">Facilitator view · {enrollments.length} leaders</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
            Live
          </div>
          {isFacilitator && (
            <button
              onClick={handleAdvance}
              disabled={advancing}
              className="bg-[#011132] text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-[#0a2254] disabled:opacity-50 transition-colors"
            >
              {advancing ? 'Ending…' : 'End Session → Consolidation'}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">

        {/* Sidebar — leader list */}
        <div className="w-52 border-r border-gray-200 bg-white overflow-y-auto flex-shrink-0">
          <div className="px-3 py-3">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2">Leaders</p>
            {enrollments.map(e => {
              const active = selectedLeader?.id === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => handleSelectLeader(e)}
                  className={`w-full text-left px-2.5 py-2.5 rounded-lg mb-0.5 transition-colors
                    ${active ? 'bg-[#011132] text-white' : 'hover:bg-gray-50 text-gray-700'}`}
                >
                  <p className={`text-sm font-semibold truncate ${active ? 'text-white' : 'text-gray-800'}`}>{e.leaderName}</p>
                  <p className={`text-[10px] truncate ${active ? 'text-white/60' : 'text-gray-400'}`}>{e.leaderJobTitle}</p>
                  <div className={`flex items-center gap-2 mt-1 text-[10px] ${active ? 'text-white/70' : 'text-gray-400'}`}>
                    <span>KR {e.krDeliveryPct}%</span>
                    {e.selfSubmitted && <span className={`${active ? 'text-white/70' : 'text-emerald-500'}`}>✓ Self</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">

          {selectedLeader ? (
            <>
              {/* Scores panel */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <div className="flex items-center gap-4 mb-4">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">{selectedLeader.leaderName}</h2>
                    <p className="text-xs text-gray-400">{selectedLeader.leaderJobTitle}</p>
                  </div>
                  <div className="flex-1" />
                  <div className="text-right">
                    <p className="text-xs text-gray-400">KR Delivery</p>
                    <p className={`text-lg font-bold ${selectedLeader.krDeliveryPct >= 80 ? 'text-emerald-600' : selectedLeader.krDeliveryPct >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                      {selectedLeader.krDeliveryPct}%
                    </p>
                  </div>
                </div>

                {/* Score legend */}
                <div className="flex items-center gap-4 mb-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-2 bg-[#011132] rounded-sm" />
                    <span className="text-gray-600">Self avg: <strong>{avgSelf ? avgSelf.toFixed(1) : '—'}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-2 bg-blue-200 rounded-sm" />
                    <span className="text-gray-600">Peer avg: <strong>{avgPeer ? avgPeer.toFixed(1) : '—'}</strong></span>
                  </div>
                </div>

                {/* Muscle bars */}
                <div className="bg-white border border-gray-200 rounded-xl px-5 py-2 mb-5">
                  {muscles.map(m => (
                    <MuscleBar
                      key={m.key}
                      muscle={m}
                      selfScore={getSelfScore(m.key)}
                      peerScore={getPeerScore(m.key)}
                    />
                  ))}
                </div>

                {/* Peer notes */}
                {liveNotes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Live Notes</p>
                    {liveNotes.map(n => (
                      <div key={n.id} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-gray-700 flex-1">{n.content}</p>
                          {n.liveScore !== undefined && (
                            <span className="text-xs font-bold text-[#011132] bg-[#011132]/5 px-1.5 py-0.5 rounded flex-shrink-0">
                              {n.liveScore}/5
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Live notes input — facilitator only */}
              {isFacilitator && <div className="w-72 border-l border-gray-200 bg-white flex flex-col flex-shrink-0">
                <div className="px-4 py-4 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Add Live Note</p>
                  <p className="text-xs text-gray-400 mt-0.5">Discussion observations for {selectedLeader.leaderName.split(' ')[0]}</p>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  <textarea
                    rows={4}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                    placeholder="Observation during discussion…"
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                  />
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Live score override (optional)</label>
                    <div className="flex items-center gap-2">
                      {[1,2,3,4,5].map(n => (
                        <button
                          key={n}
                          onClick={() => setLiveScore(prev => prev === n ? '' : n)}
                          className={`w-9 h-9 rounded-full text-sm font-bold border-2 transition-all
                            ${liveScore === n ? 'bg-[#011132] border-[#011132] text-white' : 'border-gray-200 text-gray-400 hover:border-gray-400'}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={handleAddNote}
                    disabled={!noteText.trim() || addingNote}
                    className="w-full bg-[#011132] text-white text-sm font-medium py-2 rounded-lg disabled:opacity-40 hover:bg-[#0a2254] transition-colors"
                  >
                    {addingNote ? 'Adding…' : 'Add Note'}
                  </button>
                </div>
              </div>}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-400 text-sm">Select a leader from the sidebar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderReviewLive;
